import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { CreditCard, Shield, CheckCircle, AlertCircle, ChevronRight, QrCode, Clock, RefreshCw } from 'lucide-react'
import useStore from '../store'

const PaymentPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setCurrentOrder } = useStore()
  
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState('idle') // idle, qr, processing, success, failed
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [orderId, setOrderId] = useState('')
  const [countdown, setCountdown] = useState(300) // 5分钟倒计时

  const type = searchParams.get('type') || 'precision'
  const amount = parseFloat(searchParams.get('amount') || '19.9')
  const packageId = searchParams.get('packageId')

  const typeConfig = {
    precision: {
      title: '精准匹配报告',
      description: 'AI简历分析 + 6维度雷达图 + 精准提升策略',
      amount: 19.9
    },
    package: {
      title: packageId === 'basic' ? '套餐一' : packageId === 'pro' ? '套餐二' : '套餐三',
      description: '求职服务套餐',
      amount
    }
  }

  const config = typeConfig[type] || typeConfig.precision

  // 生成订单号
  const generateOrderId = () => {
    return 'ORD' + Date.now() + Math.random().toString(36).substr(2, 4).toUpperCase()
  }

  // 创建支付订单并获取二维码
  const createPayment = async () => {
    setIsProcessing(true)
    const newOrderId = generateOrderId()
    setOrderId(newOrderId)

    try {
      // 调用后端创建支付
      const response = await fetch('/api/payment/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: newOrderId,
          amount: config.amount,
          description: config.title,
          type: type,
          packageId: packageId
        })
      })

      const data = await response.json()
      
      if (data.status === 200 && data.data?.qrCode) {
        setQrCodeUrl(data.data.qrCode)
        setPaymentStatus('qr')
        setCountdown(300)
      } else {
        // 如果后端没有返回二维码，使用模拟二维码
        generateMockQR(newOrderId)
      }
    } catch (error) {
      console.error('创建支付失败:', error)
      // 使用模拟二维码作为fallback
      generateMockQR(newOrderId)
    } finally {
      setIsProcessing(false)
    }
  }

  // 生成模拟二维码（用于演示）
  const generateMockQR = (oid) => {
    // 使用QRCode API生成一个包含订单信息的二维码
    const qrData = `weixin://wxpay/bizpayurl?pr=${oid}`
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`
    setQrCodeUrl(qrApiUrl)
    setPaymentStatus('qr')
    setCountdown(300)
  }

  // 模拟支付成功（用于测试）
  const simulatePayment = async () => {
    setPaymentStatus('processing')
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      const order = {
        id: orderId,
        type,
        packageId,
        amount: config.amount,
        status: 'paid',
        createdAt: new Date().toISOString()
      }
      setCurrentOrder(order)
      setPaymentStatus('success')
      
      setTimeout(() => {
        if (type === 'precision') {
          navigate('/precision')
        } else {
          navigate('/')
        }
      }, 2000)
    } catch (error) {
      setPaymentStatus('failed')
    }
  }

  // 倒计时
  useEffect(() => {
    let timer
    if (paymentStatus === 'qr' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setPaymentStatus('failed')
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [paymentStatus, countdown])

  // 格式化倒计时
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  if (paymentStatus === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mb-6">
          <CheckCircle size={64} className="mx-auto text-green-500" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">支付成功！</h2>
        <p className="text-gray-600 mb-6">订单号: {useStore.getState().currentOrder?.id}</p>
        <p className="text-gray-500">正在跳转...请稍候</p>
      </div>
    )
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <AlertCircle size={64} className="mx-auto text-red-500 mb-6" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">支付失败或超时</h2>
        <p className="text-gray-600 mb-6">二维码已过期或支付未完成</p>
        <button onClick={() => setPaymentStatus('idle')} className="btn-primary">
          重新支付
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <h1 className="section-title">确认订单</h1>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-6">
        {/* 订单信息 */}
        <div className="border-b border-gray-200 pb-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">商品名称</span>
            <span className="font-medium">{config.title}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">商品描述</span>
            <span className="text-sm text-gray-500 text-right max-w-[60%]">{config.description}</span>
          </div>
        </div>

        {/* 价格 */}
        <div className="flex justify-between items-center mb-6">
          <span className="text-gray-600">实付金额</span>
          <span className="text-2xl font-bold text-primary-600">¥{config.amount}</span>
        </div>

        <AnimatePresence mode="wait">
          {paymentStatus === 'qr' ? (
            <motion.div
              key="qr"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center"
            >
              {/* 二维码区域 */}
              <div className="bg-white p-4 rounded-lg border-2 border-primary-200 mb-4">
                <div className="flex items-center justify-center mb-3">
                  <QrCode className="text-primary-600 mr-2" size={20} />
                  <span className="font-medium text-gray-800">微信扫码支付</span>
                </div>
                
                {qrCodeUrl ? (
                  <div className="flex justify-center">
                    <img 
                      src={qrCodeUrl} 
                      alt="支付二维码" 
                      className="w-48 h-48"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23f0f0f0"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">二维码加载失败</text></svg>'
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-gray-100 flex items-center justify-center mx-auto">
                    <RefreshCw className="animate-spin text-gray-400" size={32} />
                  </div>
                )}
                
                <div className="mt-3 flex items-center justify-center text-sm text-gray-500">
                  <Clock size={14} className="mr-1" />
                  <span>二维码有效期: {formatCountdown(countdown)}</span>
                </div>
              </div>

              {/* 模拟支付按钮（仅用于测试） */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-xs text-gray-400 mb-3">测试用: 模拟支付成功</p>
                <button
                  onClick={simulatePayment}
                  className="w-full py-2 px-4 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  模拟支付成功
                </button>
              </div>

              <button
                onClick={() => setPaymentStatus('idle')}
                className="mt-4 text-sm text-gray-500 hover:text-primary-600"
              >
                取消支付
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* 支付方式 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">支付方式</label>
                <div className="p-4 border-2 border-primary-500 rounded-lg bg-primary-50 flex items-center">
                  <CreditCard className="text-primary-600 mr-3" size={24} />
                  <div>
                    <div className="font-medium">微信支付</div>
                    <div className="text-xs text-gray-500">推荐使用</div>
                  </div>
                  <CheckCircle className="ml-auto text-primary-600" size={20} />
                </div>
              </div>

              {/* 支付按钮 */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={createPayment}
                disabled={isProcessing}
                className="w-full btn-primary text-lg py-4 flex items-center justify-center"
              >
                {isProcessing ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    正在生成二维码...
                  </span>
                ) : (
                  <span className="flex items-center">
                    确认支付 ¥{config.amount}
                    <ChevronRight size={20} className="ml-2" />
                  </span>
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 安全提示 */}
        <div className="mt-4 flex items-center justify-center text-xs text-gray-400">
          <Shield size={14} className="mr-1" />
          <span>支付安全由微信支付保障 | 支持退款</span>
        </div>
      </motion.div>
    </div>
  )
}

export default PaymentPage
